import { StatusTypes } from '../core/models/events-models';
import { restRequest } from '../internal/rest/rest';
export class Provider {
  constructor(session) {
    this.providerIdentifier = {
      providerId: '',
      providerName: '',
    };
    this.providerConfig = undefined;
    this.ws = null;
    this.purposefulClose = false;
    this.creatingNewSocket = false;
    this.missedPongs = 0;
    this.userMsg = false;
    this.waitingOnMsgForAck = -1;
    this.msgQueue = [];
    this.isTyping = false;
    this.session = session;
  }
  startChat(token) {
    if (this.getConnectionStatus() !== WebSocket.CLOSED) {
      this.session.chatInstance.emitError(
        'CONNECTION_FAILURE',
        'Connection already exists.',
      );
      return;
    } else if (this.providerConfig === undefined) {
      this.session.chatInstance.emitError(
        'PROVIDER_UNAVAILABLE',
        'Availability did not succeed.',
      );
      return;
    }
    this.latestToken = token;
    this.session.chatInstance.emitStatusChange(
      this.providerIdentifier,
      StatusTypes.CONNECTING,
    );
    this.establishConnection();
  }
  async checkAvailability(token, providerId, request) {
    var _a;
    this.latestToken = token;
    this.providerIdentifier.providerId = providerId;
    let data;
    try {
      data = await restRequest(
        `https://${this.session.config.environment}/digital/v2/chat/api/setup/${providerId}?version=${this.session.config.version}`,
        'POST',
        request,
        token,
      );
    } catch (e) {}
    if (data === null || data === void 0 ? void 0 : data.isAvailable) {
      this.providerConfig = data.config;
      this.providerIdentifier = data.provider;
      this.providerGatewayData = data.data;
      return {
        isAvailable: data.isAvailable,
        unavailableReason: data.unavailableReason,
      };
    }
    return {
      isAvailable: false,
      unavailableReason:
        (_a =
          data === null || data === void 0
            ? void 0
            : data.unavailableReason) !== null && _a !== void 0
          ? _a
          : 'BAD AVAILABILITY DATA',
    };
  }
  closeChat() {
    this.purposefulClose = true;
    if (this.getConnectionStatus() !== WebSocket.CLOSED) {
      this.onChatClose();
      this.session.chatInstance.emitStatusChange(
        this.providerIdentifier,
        StatusTypes.DISCONNECTED,
      );
      this.ws.close();
    } else if (
      this.session.chatInstance.getCurrentChatState() ===
      StatusTypes.RECONNECTING
    ) {
      this.endReconnect();
      this.session.chatInstance.emitStatusChange(
        this.providerIdentifier,
        StatusTypes.DISCONNECTED,
      );
    }
  }
  sendIsTyping() {
    if (this.getConnectionStatus() === WebSocket.CLOSED) {
      this.session.chatInstance.emitError(
        'MESSAGE_SEND_FAILURE',
        'Web Socket is in closed state.',
      );
      return;
    } else if (!this.isTyping) {
      this.sendIsTypingHandler();
      this.isTyping = true;
      setTimeout(
        () => (this.isTyping = false),
        this.providerConfig.isTypingInterval,
      );
    }
  }
  sendEvent(token, event) {
    this.latestToken = token;
    if (!event || !event.name) {
      this.session.chatInstance.emitError(
        'EVENT_SEND_FAILURE',
        'Invalid event.',
      );
      return;
    }
    this.userMsg = true;
    this.sendEventHandler(event, token);
    this.session.chatInstance.incrementMessageId();
    return (this.session.chatInstance.getCurrentMessageId() - 1).toString();
  }
  sendMessage(token, text) {
    this.latestToken = token;
    if (!text) {
      this.session.chatInstance.emitError(
        'MESSAGE_SEND_FAILURE',
        'Invalid message.',
      );
      return;
    } else if (text.length > this.providerConfig.messageCharacterLimit) {
      this.session.chatInstance.emitError(
        'MESSAGE_SEND_MAX_LENGTH',
        'Max message character length exceeded.',
      );
      return;
    }
    this.userMsg = true;
    this.sendMessageHandler(text, token);
    this.session.chatInstance.incrementMessageId();
    return (this.session.chatInstance.getCurrentMessageId() - 1).toString();
  }
  getConnectionStatus() {
    if (!this.ws) {
      return WebSocket.CLOSED;
    }
    return this.ws.readyState;
  }
  establishConnection() {
    try {
      this.pongReceived = true;
      this.ws = new WebSocket(this.providerConfig.chatURL, this.latestToken);
      this.ws.onerror = (_error) => {
        this.ws.close();
      };
      this.ws.onopen = () => {
        this.setPingInterval();
        if (
          this.session.chatInstance.getCurrentChatState() !==
          StatusTypes.RECONNECTING
        ) {
          this.session.chatInstance.emitChatStart();
          this.onInitiateChat();
          return;
        }
        this.endReconnect();
        this.onReconnectChat();
      };
      this.ws.onclose = () => {
        this.ws = null;
        window.clearInterval(this.pingInterval);
        if (
          this.purposefulClose ||
          this.session.chatInstance.getCurrentChatState() ===
            StatusTypes.DISCONNECTED ||
          this.session.chatInstance.getCurrentChatState() === StatusTypes.ERROR
        ) {
          this.endReconnect();
          return;
        } else if (
          this.session.chatInstance.getCurrentChatState() ===
          StatusTypes.CONNECTING
        ) {
          this.session.chatInstance.emitStatusChange(
            this.providerIdentifier,
            StatusTypes.DISCONNECTED,
          );
          if (this.session.chatInstance.getTransferInProgress()) {
            this.session.chatInstance.emitError(
              'TRANSFER_PROVIDER_FAILURE',
              'Something went wrong transferring the chat.',
              true,
            );
            this.session.chatInstance.transferCanceled();
          } else {
            this.session.chatInstance.emitError(
              'CONNECTION_FAILURE',
              'Something went wrong establishing the connection with the provider.',
              true,
            );
          }
          this.session.chatInstance.emitChatEnd('ERROR');
          return;
        } else if (
          this.session.chatInstance.getCurrentChatState() !==
            StatusTypes.RECONNECTING &&
          !this.creatingNewSocket
        ) {
          this.session.chatInstance.emitError(
            'CONNECTION_LOST',
            'Websocket was closed abnormally.',
          );
          this.setReconnectionTimeout();
        }
        this.creatingNewSocket = true;
      };
      this.ws.onmessage = (json) => {
        if (
          this.session.chatInstance.getCurrentChatState() ===
            StatusTypes.RECONNECTING &&
          this.reconnectInterval
        ) {
          this.session.chatInstance.emitStatusChange(
            this.providerIdentifier,
            StatusTypes.CONNECTED,
          );
          this.endReconnect();
          this.setPingInterval();
        }
        try {
          this.webSocketMessageHandler(json);
        } catch (e) {
          this.session.chatInstance.emitError(
            'GENERAL_ERROR',
            'BAD WEBSOCKET DATA',
            false,
            e,
          );
        }
      };
    } catch (e) {
      this.session.chatInstance.emitError('CONNECTION_FAILURE', `${e}`);
      this.session.chatInstance.emitStatusChange(
        this.providerIdentifier,
        StatusTypes.DISCONNECTED,
      );
    }
  }
  setPingInterval() {
    if (!this.providerConfig.supportsPingPong) {
      return;
    }
    this.pingInterval = window.setInterval(() => {
      this.sendPing();
      if (!this.pongReceived) {
        if (this.missedPongs < this.providerConfig.pingRetryLimit) {
          this.missedPongs++;
          return;
        }
        this.setReconnectionTimeout();
        this.session.chatInstance.emitError(
          'CONNECTION_LOST',
          'Ping retry limit exceeded.',
        );
        window.clearInterval(this.pingInterval);
        return;
      }
      this.missedPongs = 0;
      this.pongReceived = false;
    }, this.providerConfig.pingInterval);
  }
  signalPongReceived() {
    this.pongReceived = true;
  }
  setReconnectionTimeout() {
    this.session.chatInstance.emitStatusChange(
      this.providerIdentifier,
      StatusTypes.RECONNECTING,
    );
    this.reconnectTimeout = window.setTimeout(() => {
      var _a;
      this.session.chatInstance.emitStatusChange(
        this.providerIdentifier,
        StatusTypes.DISCONNECTED,
      );
      this.session.chatInstance.emitError(
        'CONNECTION_FAILURE',
        'Could not reconnect',
      );
      window.clearInterval(this.reconnectInterval);
      (_a = this.ws) === null || _a === void 0 ? void 0 : _a.close();
      this.session.chatInstance.emitChatEnd('ERROR');
      this.session.chatInstance.emitStatusChange(
        this.providerIdentifier,
        StatusTypes.ERROR,
      );
    }, this.providerConfig.reconnectionTimeout);
    this.reconnectInterval = window.setInterval(() => {
      if (this.creatingNewSocket) {
        this.session.chatInstance.emitStatusChange(
          this.providerIdentifier,
          StatusTypes.DISCONNECTED,
        );
        this.session.chatInstance.emitError(
          'CONNECTION_FAILURE',
          'Could not reconnect',
        );
        this.endReconnect();
        this.session.chatInstance.emitChatEnd('ERROR');
        this.session.chatInstance.emitStatusChange(
          this.providerIdentifier,
          StatusTypes.ERROR,
        );
      } else {
        this.sendPing();
      }
    }, 5000);
  }
  endReconnect() {
    this.creatingNewSocket = false;
    window.clearTimeout(this.reconnectTimeout);
    window.clearInterval(this.reconnectInterval);
    this.reconnectInterval = null;
    this.reconnectTimeout = null;
  }
  sendToWebSocket(data) {
    if (this.userMsg) {
      this.session.chatInstance.addToSentMessages(data);
      this.sendUserMessage(this.session.chatInstance.getCurrentMessageId());
      this.userMsg = false;
      return;
    }
    if (
      this.getConnectionStatus() !== WebSocket.OPEN &&
      this.getConnectionStatus() !== WebSocket.CLOSING
    ) {
      this.session.chatInstance.emitError(
        'GENERAL_ERROR',
        'Websocket not in open state.',
      );
      return;
    }
    this.ws.send(JSON.stringify(data));
  }
  sendUserMessage(msgId) {
    if (this.getConnectionStatus() !== WebSocket.OPEN) {
      this.session.chatInstance.emitError(
        'GENERAL_ERROR',
        'Websocket not in open state.',
      );
      this.session.chatInstance.emitMessageAcknowledge(msgId, 'ERROR');
      return;
    }
    const data = this.session.chatInstance.getSentMessages()[msgId].data;
    if (
      this.providerConfig.supportsProviderAcknowledgementsBySequence &&
      this.providerConfig.supportsProviderMessageDelivered &&
      this.waitingOnMsgForAck > -1
    ) {
      this.msgQueue.push(msgId);
      return;
    }
    this.ws.send(JSON.stringify(data));
    this.waitingOnMsgForAck = msgId;
    if (
      !this.providerConfig.supportsProviderAcknowledgementsById &&
      !this.providerConfig.supportsProviderAcknowledgementsBySequence &&
      !this.providerConfig.supportsProviderMessageDelivered
    ) {
      this.session.chatInstance.emitMessageAcknowledge(msgId, 'UNSUPPORTED');
      return;
    }
    if (!this.providerConfig.providerAcknowledgementTimeout) {
      return;
    }
    setTimeout(() => {
      const msg = this.session.chatInstance.getSentMessages()[msgId];
      if (
        msg.ackInfo === 'DELIVERED' ||
        this.session.chatInstance.getCurrentChatState() ===
          StatusTypes.DISCONNECTED
      ) {
        return;
      } else if (msg.resent) {
        this.session.chatInstance.emitMessageAcknowledge(msgId, 'ERROR');
        this.waitingOnMsgForAck = -1;
        return;
      }
      msg.resent = true;
      this.waitingOnMsgForAck = -1;
      this.sendUserMessage(msgId);
    }, this.providerConfig.providerAcknowledgementTimeout);
  }
  messageAcknowledgementReceived(msgId) {
    if (
      this.providerConfig.supportsProviderAcknowledgementsById &&
      this.providerConfig.supportsProviderMessageDelivered
    ) {
      this.session.chatInstance.emitMessageAcknowledge(msgId, 'DELIVERED');
    } else if (
      this.providerConfig.supportsProviderAcknowledgementsBySequence &&
      this.providerConfig.supportsProviderMessageDelivered &&
      this.waitingOnMsgForAck >= 0
    ) {
      const queuedMsgId = this.msgQueue.shift();
      this.session.chatInstance.emitMessageAcknowledge(
        this.waitingOnMsgForAck,
        'DELIVERED',
      );
      this.waitingOnMsgForAck = -1;
      if (queuedMsgId !== undefined) {
        this.sendUserMessage(queuedMsgId);
      }
    }
  }
}
//# sourceMappingURL=provider-abstract.js.map
